/**
 * Where a custom object's 3D model sits — shared by the Object Editor's 3D
 * view and placed objects in projects, so what the editor shows is what
 * projects render.
 *
 * World matrix = Frame × LiveScale × T(position) × R(rotation) × S(scale) × T(−pivot):
 *  - (position, rotation, scale) place the model in the object's authored
 *    frame at its DEFAULT size: u along the width, n along the depth, z up
 *    from the floor (model3d in the spec); `pivot` is the model's origin —
 *    the point of the file that `position` places and that rotation and
 *    scale turn around;
 *  - LiveScale follows the placed object's size per axis like the 2D
 *    groups do: `stretch` = size ratio, `fixed` = 1, `uniform` = the
 *    smallest ratio of all three axes (no distortion);
 *  - Frame puts the object's center on the floor at its world position and
 *    orientation (dir = +u, norm = +n).
 */

import * as THREE from 'three';
import type { ObjectDefinitionSize, ObjectModel3D, ObjectModelScaleMode, Vec3 } from 'shared-types';
import type { SketchObjectFrame } from '../types';

export interface LiveSize {
  width: number;
  depth: number;
  height: number;
}

/** Per-axis live scale factors [u, n, h]. */
export function modelLiveScale(model: ObjectModel3D, defaultSize: ObjectDefinitionSize, live: LiveSize): [number, number, number] {
  const ratio = (value: number, base: number): number => (base > 1e-9 ? value / base : 1);
  const ratios: [number, number, number] = [ratio(live.width, defaultSize.width), ratio(live.depth, defaultSize.depth), ratio(live.height, defaultSize.height)];
  const uniform = Math.min(...ratios);
  const pick = (mode: ObjectModelScaleMode, r: number): number => (mode === 'stretch' ? r : mode === 'uniform' ? uniform : 1);
  return [pick(model.scaleMode.u, ratios[0]), pick(model.scaleMode.n, ratios[1]), pick(model.scaleMode.h, ratios[2])];
}

/** The model's placement in the authored frame (T × R × S × T(−pivot)). */
export function modelLocalMatrix(model: ObjectModel3D): THREE.Matrix4 {
  const placement = new THREE.Matrix4().compose(
    new THREE.Vector3(...model.position),
    new THREE.Quaternion().setFromEuler(new THREE.Euler(model.rotation[0], model.rotation[1], model.rotation[2], 'XYZ')),
    new THREE.Vector3(...model.scale),
  );
  const pivot = model.pivot;
  return pivot ? placement.multiply(new THREE.Matrix4().makeTranslation(-pivot[0], -pivot[1], -pivot[2])) : placement;
}

const round5 = (v: number): number => Math.round(v * 1e5) / 1e5;

/**
 * Move the model's origin to `target` (authored frame) WITHOUT moving the
 * model: the origin's place becomes `target`, and the pivot (file
 * coordinates) moves by the same step turned back through rotation and
 * scale. A zero scale can't be undone: the placement stays as it is.
 */
export function moveModelOrigin(model: ObjectModel3D, target: THREE.Vector3): ObjectModel3D {
  const rotationScale = new THREE.Matrix4().compose(new THREE.Vector3(), new THREE.Quaternion().setFromEuler(new THREE.Euler(...model.rotation, 'XYZ')), new THREE.Vector3(...model.scale));
  if (Math.abs(rotationScale.determinant()) < 1e-12) return model;
  const step = target.clone().sub(new THREE.Vector3(...model.position)).applyMatrix4(rotationScale.invert());
  const pivot = new THREE.Vector3(...(model.pivot ?? [0, 0, 0])).add(step);
  return { ...model, position: [round5(target.x), round5(target.y), round5(target.z)], pivot: [round5(pivot.x), round5(pivot.y), round5(pivot.z)] };
}

/** The model's bounding box in the authored frame, from its own (file-space) `bounds`. */
export function placedModelBounds(model: ObjectModel3D, bounds: THREE.Box3): THREE.Box3 {
  return bounds.clone().applyMatrix4(modelLocalMatrix(model));
}

/** Frame matrix: object center on the floor at `floorZ`, axes dir / norm / up. */
export function frameMatrix(frame: SketchObjectFrame, floorZ: number): THREE.Matrix4 {
  return new THREE.Matrix4().set(frame.dir.x, frame.norm.x, 0, frame.center.x, frame.dir.y, frame.norm.y, 0, frame.center.y, 0, 0, 1, floorZ, 0, 0, 0, 1);
}

export function modelWorldMatrix(model: ObjectModel3D, defaultSize: ObjectDefinitionSize, frame: SketchObjectFrame, height: number, floorZ: number): THREE.Matrix4 {
  const [fu, fn, fh] = modelLiveScale(model, defaultSize, { width: frame.width, depth: frame.depth, height });
  return frameMatrix(frame, floorZ).multiply(new THREE.Matrix4().makeScale(fu, fn, fh)).multiply(modelLocalMatrix(model));
}

/** Rotation turning a model's up axis to +z: glTF/OBJ/FBX/PLY/IFC are +Y up. */
export const upAxisRotation = (yUp: boolean): Vec3 => [yUp ? Math.PI / 2 : 0, 0, 0];

/**
 * Placement fitting a model (its own `bounds`, turned by `rotationXYZ`)
 * into the default footprint and height: uniformly scaled, standing
 * centered on the floor. A moved origin (`pivot`) stays where it is on the model.
 */
export function fitModelPlacement(bounds: THREE.Box3, rotationXYZ: Vec3, size: ObjectDefinitionSize, pivot?: Vec3): Pick<ObjectModel3D, 'position' | 'rotation' | 'scale'> {
  const rotation = new THREE.Euler(rotationXYZ[0], rotationXYZ[1], rotationXYZ[2], 'XYZ');
  const rotated = bounds.clone().applyMatrix4(new THREE.Matrix4().makeRotationFromEuler(rotation));
  const extent = rotated.getSize(new THREE.Vector3());
  const fits = [extent.x > 1e-9 ? size.width / extent.x : Infinity, extent.y > 1e-9 ? size.depth / extent.y : Infinity, extent.z > 1e-9 ? size.height / extent.z : Infinity];
  const finite = fits.filter(Number.isFinite);
  const s = finite.length > 0 ? Math.min(...finite) : 1;
  const center = rotated.getCenter(new THREE.Vector3());
  const round = (v: number): number => Math.round(v * 1e5) / 1e5;
  // Scaled about the file's origin: move its bottom-center to the frame origin …
  const position = new THREE.Vector3(-center.x * s, -center.y * s, -rotated.min.z * s);
  // … and with a moved origin, place that point where it now lands.
  if (pivot) position.add(new THREE.Vector3(...pivot).applyEuler(rotation).multiplyScalar(s));
  return {
    rotation: [round(rotation.x), round(rotation.y), round(rotation.z)],
    scale: [round(s), round(s), round(s)],
    position: [round(position.x), round(position.y), round(position.z)],
  };
}
